import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";

import UrlGisHierarchyContext from "./url-gis-hierarchy-context";

import { getAllGisHierarchyItemsByHierarchyId } from "services/requests/v1/gishierarchyitem";

import {
    GisLayerTableType,
    HierarchyItemType,
    SetRequestError,
    SetStateType,
    SubscribeContentType,
    UnsubscribeContentType,
} from "types/generic";
import {
    useRequestStatusManager,
    RequestStatusManagerErrorListType,
} from "utils/hooks/use-request-status-manager.hook";

interface GisLayersTreeHierarchyContextModel {
    gisHierarchyId: HierarchyItemType[];
    dataHierarchy: HierarchyItemType[];
    gisHierarchySelected: HierarchyItemType[];
    gisHierarchySelectedObject: GisLayerTableType[] | undefined;
    allContentIsReady: boolean;
    errorList: RequestStatusManagerErrorListType;
    success: boolean;
    contentName: string;
    subscribeContent: SubscribeContentType;
    unsubscribeContent: UnsubscribeContentType;
    setError: SetRequestError;
    removeAllData: () => void;
    setGisHierarchyId: SetStateType<HierarchyItemType[]>;
    setDataHierarchy: SetStateType<HierarchyItemType[]>;
    setGisHierarchySelected: SetStateType<HierarchyItemType[]>;
    setGisHierarchySelectedObject: SetStateType<
        GisLayerTableType[] | undefined
    >;
    loadHierarchiesById: () => Promise<void>;
}

const initialData: GisLayersTreeHierarchyContextModel = {
    dataHierarchy: [],
    gisHierarchyId: [],
    gisHierarchySelected: [],
    gisHierarchySelectedObject: [],
    allContentIsReady: true,
    errorList: [],
    success: false,
    contentName: "",
    setError: () => {},
    subscribeContent: () => {},
    unsubscribeContent: () => {},
    removeAllData: () => {},
    setDataHierarchy: () => {},
    setGisHierarchyId: () => {},
    setGisHierarchySelected: () => {},
    setGisHierarchySelectedObject: () => {},
    loadHierarchiesById: async () => {},
};

export const GisLayersTreeHierarchyContext =
    React.createContext<GisLayersTreeHierarchyContextModel>(initialData);

const GisLayersTreeHierarchyProvider: React.FC = (props) => {
    const [gisHierarchyId, setGisHierarchyId] = useState<HierarchyItemType[]>(
        []
    );

    const [dataHierarchy, setDataHierarchy] = useState<HierarchyItemType[]>([]);

    const [gisHierarchySelected, setGisHierarchySelected] = useState<
        HierarchyItemType[]
    >([]);

    const [gisHierarchySelectedObject, setGisHierarchySelectedObject] =
        useState<GisLayerTableType[] | undefined>([]);

    const urlGisCtx = useContext(UrlGisHierarchyContext);

    const {
        allContentIsReady,
        errorList,
        success,
        subscribeContent,
        unsubscribeContent,
        removeAllData,
        setError,
    } = useRequestStatusManager();

    const contentName = "gisPageTree";

    const loadHierarchiesById = async () => {
        subscribeContent(contentName);

        try {
            const { data } = await getAllGisHierarchyItemsByHierarchyId(1);
            setGisHierarchyId(data);
            unsubscribeContent(contentName);
        } catch (error) {
            setError(contentName);
        }
    };

    useEffect(() => {
        loadHierarchiesById();
        urlGisCtx.showPanel = false;
    }, []);

    return (
        <GisLayersTreeHierarchyContext.Provider
            value={{
                dataHierarchy,
                gisHierarchyId,
                gisHierarchySelected,
                gisHierarchySelectedObject,
                allContentIsReady,
                errorList,
                success,
                contentName,
                setError,
                subscribeContent,
                unsubscribeContent,
                removeAllData,
                loadHierarchiesById,
                setDataHierarchy,
                setGisHierarchyId,
                setGisHierarchySelected,
                setGisHierarchySelectedObject,
            }}
        >
            {props.children}
        </GisLayersTreeHierarchyContext.Provider>
    );
};

GisLayersTreeHierarchyProvider.propTypes = {
    children: PropTypes.node,
};

export const useGisLayersTreeContext = () =>
    useContext(GisLayersTreeHierarchyContext);

export default GisLayersTreeHierarchyProvider;
